/**
 * /help/:slug — renders a single KB article.
 *
 * Markdown body is rendered with react-markdown + remark-gfm (tables,
 * task lists, strikethrough). The TOC sidebar lists other articles in
 * the same category so users can flip between adjacent topics without
 * going back to the index.
 *
 * Slug not found OR slug gated by role the user doesn't have → both
 * render the same "article not found" state. We intentionally don't
 * leak whether an article exists-but-is-gated.
 */

import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, BookOpen, ChevronRight } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { articleBySlug, articlesForRole } from '@/help/registry';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export default function HelpArticle() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const role = user?.role || 'viewer';

  const article = useMemo(() => articleBySlug(slug, role), [slug, role]);
  const sameCategory = useMemo(() => {
    if (!article) return [];
    return articlesForRole(role)
      .filter((a) => a.category === article.category && a.slug !== article.slug);
  }, [article, role]);

  // While auth resolves, hold off — the role-gating check below would
  // otherwise default to 'viewer' and falsely 404 admin-only articles.
  if (loading) return null;

  if (!article) {
    return (
      <div className="space-y-6 max-w-3xl" data-testid="help-article-not-found">
        <Link to="/help" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to help centre
        </Link>
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <BookOpen className="h-10 w-10 text-muted-foreground mx-auto" />
            <h2 className="text-lg font-semibold">Article not found</h2>
            <p className="text-sm text-muted-foreground">
              We couldn't find an article at this address. It may have been
              renamed, or it's only visible to certain roles.
            </p>
            <Link to="/help">
              <Button variant="outline">Browse all articles</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-8 max-w-6xl" data-testid="help-article">
      {/* Main column */}
      <article className="space-y-6 min-w-0">
        <div>
          <Link to="/help" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Help centre
          </Link>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mt-3">
            {article.category}
          </p>
          <h1 className="text-3xl font-semibold mt-1" data-testid="help-article-title">
            {article.title}
          </h1>
        </div>

        {/*
          react-markdown renders the body. We use Tailwind's typography defaults
          via the `prose` class, scoped to LinkHub-friendly sizing.
        */}
        <div
          className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-pre:bg-muted prose-code:before:hidden prose-code:after:hidden prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
          data-testid="help-article-body"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body}</ReactMarkdown>
        </div>
      </article>

      {/* Same-category nav */}
      {sameCategory.length > 0 && (
        <aside className="space-y-3">
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
            More in {article.category}
          </h2>
          <nav className="space-y-1">
            {sameCategory.map((other) => (
              <Link
                key={other.slug}
                to={`/help/${other.slug}`}
                className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                data-testid={`help-related-link-${other.slug}`}
              >
                <span>{other.title}</span>
                <ChevronRight className="h-4 w-4" />
              </Link>
            ))}
          </nav>
        </aside>
      )}
    </div>
  );
}

// Re-export for cases where a future consumer wants to redirect to the
// index when a slug is empty — keeps the import surface small.
export const HelpArticleRedirectToIndex = () => <Navigate to="/help" replace />;
